/**
 * NativeBridge — mounts inside the router so it can call `useNavigate`,
 * `useSession`, and the notification / toast contexts. Wires the
 * platform lifecycle abstraction into the running React tree.
 *
 * Responsibilities:
 *   - App resume → revalidate the active charging session via the
 *     SessionContext `rehydrateSession()` helper. The rehydration
 *     logic already guards against duplicate polling + SSE loops via
 *     the existing sessionUid useEffect, so calling it repeatedly is
 *     idempotent — see SessionContext.jsx.
 *   - Deep links → resolve and navigate via React Router. Cold-start
 *     links replace history so the browser back button behaves
 *     correctly on the first navigation.
 *   - Android back button → close the notification drawer if it's
 *     open, then let normal history navigation take over. The
 *     "swallow arbitrary back presses" hazard is contained inside
 *     lifecycle.js's LIFO handler chain, so React back-button
 *     consumers stack cleanly without conflict.
 *   - Network changes → surface a single, calm toast when connection
 *     drops or comes back. Repeated flapping is coalesced by the
 *     network abstraction's debounce.
 *
 * The component renders nothing.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOptionalSession } from '../context/SessionContext.jsx';
import { useOptionalToast } from '../context/ToastContext.jsx';
import { useOptionalNotificationDrawer } from '../shell/NotificationDrawer/NotificationDrawerContext.jsx';
import { useNativeAppLifecycle } from './useNativeAppLifecycle.jsx';
import { isNative } from './platform.js';

export default function NativeBridge() {
  const navigate = useNavigate();
  const session = useOptionalSession();
  const toast = useOptionalToast();
  const drawer = useOptionalNotificationDrawer();

  // Grab the freshest session helpers via a ref so the lifecycle
  // useEffect deps stay stable — the callback identities are already
  // memoised inside SessionProvider, but re-registering the resume
  // listener on every render is a leak we explicitly want to avoid.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;

  const handleResume = useCallback(() => {
    const s = sessionRef.current;
    const uid = s?.state?.sessionUid;
    if (uid && typeof s.rehydrateSession === 'function') {
      // rehydrateSession is guarded against transient errors — it
      // will not wipe local state on a network blip. See
      // SessionContext.jsx around the `transient: true` branch.
      s.rehydrateSession(uid).catch(() => {});
    }
  }, []);

  const handleDeepLink = useCallback((resolved, meta) => {
    if (!resolved || !resolved.path) return;
    try { navigate(resolved.path, { replace: meta?.source === 'cold' }); }
    catch { /* router unmounted */ }
  }, [navigate]);

  const handleBack = useCallback(() => {
    const d = drawerRef.current;
    if (d && d.isOpen) {
      try { d.close(); } catch { /* ignore */ }
      return true;
    }
    return false;
  }, []);

  const lastConnectedRef = useRef(null);
  const handleNetworkChange = useCallback((status) => {
    const nowConnected = status?.connected !== false;
    const prevConnected = lastConnectedRef.current;
    // On first tick just record the state — don't announce it.
    if (prevConnected === null) {
      lastConnectedRef.current = nowConnected;
      return;
    }
    if (nowConnected === prevConnected) return;
    lastConnectedRef.current = nowConnected;
    // Calm toast, matches the current design language. Never spam —
    // the abstraction already debounces flapping.
    if (!nowConnected) {
      toast.showToast({ message: "You're offline. Some features may not work.", type: 'warning', duration: 5000 });
    } else {
      toast.showToast({ message: 'Back online', type: 'success', duration: 3000 });
    }
  }, [toast]);

  useNativeAppLifecycle({
    onResume: handleResume,
    onDeepLink: handleDeepLink,
    onBack: handleBack,
    onNetworkChange: handleNetworkChange,
  });

  // Also fire a resume when the browser tab regains focus (web
  // parity). SessionContext handles the deduplication.
  useEffect(() => {
    if (isNative()) return undefined;
    const onVisibility = () => { if (!document.hidden) handleResume(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [handleResume]);

  return null;
}
